/* eslint-disable no-else-return */
/* eslint-disable no-unsafe-optional-chaining */
import {
  DRIVER_PANEL_ACTION_COL_WIDTH,
  PLAN_DRIVER_TABLE_ROW_HEIGHT,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planInputPanelTheme';

type TableVariant = 'legacy' | 'revamp';

const LEGACY_ROW_HEIGHT = 50;
const REVAMP_ROW_HEIGHT = Number.parseInt(PLAN_DRIVER_TABLE_ROW_HEIGHT, 10);
const REVAMP_ACTION_COL_WIDTH = Number.parseInt(DRIVER_PANEL_ACTION_COL_WIDTH, 10);
const REVAMP_MIN_COL_WIDTH = 120;
const REVAMP_DATE_COL_WIDTH = 118;
const REVAMP_DROPDOWN_COL_MIN = 156;
const REVAMP_FILTER_ICON_SPACE = 52;
const REVAMP_NAME_MIN_WIDTH = 148;

const rowHeightFor = (variant: TableVariant) =>
  variant === 'revamp' ? REVAMP_ROW_HEIGHT : LEGACY_ROW_HEIGHT;

const isDateProperty = (name: string) => {
  const lower = name.trim().toLowerCase();
  return lower === 'start' || lower === 'end' || lower === 'start date' || lower === 'end date';
};

const lastIndexClass = (index: number, total: number, variant: TableVariant) =>
  variant === 'legacy' && index === total - 1 ? ' last-index ' : '';

const headerRow = (
  tableItemsProperty_: any,
  tableProperty_: any,
  variant: TableVariant = 'legacy',
) => {
  return {
    rowId: 'header',
    height: rowHeightFor(variant),
    nonEditable: true,
    cells: [
      {
        type: 'filter',
        id: 'name',
        text: 'Name',
        hasDelete: false,
        hasEdit: false,
        hasFiltersApplied: false,
        nonEditable: true,
      },
      ...(tableProperty_?.length
        ? tableProperty_?.map((data: any, index: any) => {
            const row = {
              type: 'filter',
              id: data?.id,
              hasFiltersApplied: false,
              hasDelete: true,
              hasEdit: true,
              colType: 'header',
              text: data?.name,
              nonEditable: true,
              className: lastIndexClass(index, tableProperty_?.length, variant),
            };
            return row;
          })
        : []),
      { type: 'header', text: 'Del', nonEditable: true },
    ],
  };
};

const footerRow = (tableProperty_: any, variant: TableVariant = 'legacy') => {
  return {
    rowId: 'footer',
    height: rowHeightFor(variant),
    nonEditable: true,
    cells: [
      { type: 'addRow', text: '', className: 'footer', nonEditable: true },
      ...(tableProperty_?.length
        ? tableProperty_?.map((data: any, index: number) => {
            const row = {
              type: 'text',
              text: '',
              className: `${lastIndexClass(index, tableProperty_?.length, variant)}footer`,
              nonEditable: true,
              hasFormulaError: false,
            };
            return row;
          })
        : []),
      {
        type: 'text',
        text: '',
        className: 'footer last-footer',
        nonEditable: true,
        hasFormulaError: false,
      },
    ],
  };
};

const getRows = (
  tableItemsProperty_: any,
  tableProperty_: any,
  variant: TableVariant = 'legacy',
) => {
  const rowHeight = rowHeightFor(variant);

  return [
    headerRow(tableItemsProperty_, tableProperty_, variant),
    ...(tableItemsProperty_?.length
      ? tableItemsProperty_?.map((item: any, index: any) => {
          return {
            rowId: index,
            height: rowHeight,
            itemId: item?.id,
            itemName: item?.name,
            cells: [
              {
                type: 'text',
                placeholder: 'Enter value',
                text: `${item?.name}` ?? '',
                property: 'name',
                rows: tableItemsProperty_?.length,
                className: 'name',
                hasFormulaError: false,
                index,
              },
              ...(tableProperty_?.length
                ? tableProperty_?.map((data: any, index_: any) => {
                    if (isDateProperty(data?.name)) {
                      const row = {
                        type: 'date',
                        text: item[`${data?.id}`] || '',
                        date: new Date(),
                        nonEditable: !item?.id,
                        className: lastIndexClass(index_, tableProperty_?.length, variant),
                      };
                      return row;
                    } else if (data?.items) {
                      const row = {
                        className: lastIndexClass(index_, tableProperty_?.length, variant),
                        type: 'dropdown',
                        nonEditable: !item?.id,
                        selectOption: true,
                        selectedValue: item[`${data?.id}`],
                        values: [
                          ...(data?.items?.length
                            ? data?.items?.map((value: any) => ({
                                label: value?.name,
                                value: value?.name,
                              }))
                            : []),
                        ],
                        index,
                        operation: '',
                        data,
                      };
                      return row;
                    } else {
                      const row = {
                        type: 'text',
                        rows: tableItemsProperty_?.length,
                        text: item[`${data?.id}`] ?? '',
                        index,
                        data,
                        nonEditable: !item?.id,
                        className: lastIndexClass(index_, tableProperty_?.length, variant),
                        hasFormulaError: false,
                      };
                      return row;
                    }
                  })
                : []),
              {
                type: 'dropdown',
                selectOption: true,
                buttonType: 1,
                id: item?.id,
                itemName: item?.name,
                nonEditable: !item?.id,
                values: [{ label: 'Delete row', value: '' }],
              },
            ],
          };
        })
      : []),
    footerRow(tableProperty_, variant),
  ];
};

const calculateWidth = (
  totalElements: number,
  totalWidth: number,
  variant: TableVariant = 'legacy',
) => {
  const nameField: number = variant === 'revamp' ? REVAMP_NAME_MIN_WIDTH : 150;
  const editField: number = variant === 'revamp' ? REVAMP_ACTION_COL_WIDTH : 60;
  const minDataColWidth = variant === 'revamp' ? REVAMP_MIN_COL_WIDTH : 150;
  const columnsWidth = totalElements * minDataColWidth + nameField + editField;
  return totalWidth > columnsWidth
    ? (totalWidth - editField) / (totalElements + 1)
    : minDataColWidth;
};

const calculateColumnWidth = (name: string, variant: TableVariant = 'legacy', data?: any) => {
  if (variant === 'revamp') {
    if (isDateProperty(name)) {
      return REVAMP_DATE_COL_WIDTH;
    }
    if (data?.items) {
      return Math.max(REVAMP_DROPDOWN_COL_MIN, name.length * 8 + REVAMP_FILTER_ICON_SPACE);
    }
    return Math.max(REVAMP_MIN_COL_WIDTH, name.length * 8 + REVAMP_FILTER_ICON_SPACE);
  }
  const baseWidth = 150;
  const multiplier = 12;
  return Math.max(baseWidth, name.length * multiplier);
};

const distributeRevampColumnWidths = (
  tableProperty_: any[],
  tableReference: number,
): { nameWidth: number; dataWidths: number[] } => {
  const dataWidths = tableProperty_.map((data: any) =>
    calculateColumnWidth(data?.name, 'revamp', data),
  );
  const fixedTotal =
    REVAMP_NAME_MIN_WIDTH + REVAMP_ACTION_COL_WIDTH + dataWidths.reduce((sum, w) => sum + w, 0);

  if (tableReference <= fixedTotal) {
    return { nameWidth: REVAMP_NAME_MIN_WIDTH, dataWidths };
  }

  const extra = tableReference - fixedTotal;
  const share = Math.floor(extra / (tableProperty_.length + 1));
  const nameWidth = REVAMP_NAME_MIN_WIDTH + share;

  return {
    nameWidth,
    dataWidths: dataWidths.map((width) => width + share),
  };
};

const getColumns = (tableProperty_: any, tableReference: any, variant: TableVariant = 'legacy') => {
  const actionColWidth = variant === 'revamp' ? REVAMP_ACTION_COL_WIDTH : 60;

  if (variant === 'revamp' && tableReference) {
    const { nameWidth, dataWidths } = distributeRevampColumnWidths(
      tableProperty_ ?? [],
      tableReference,
    );

    return [
      { columnId: 'name', width: nameWidth, resizable: true },
      ...(tableProperty_?.length
        ? tableProperty_?.map((data: any, index: number) => ({
            columnId: `${data?.id}`,
            width: dataWidths[index],
            resizable: true,
          }))
        : []),
      { columnId: 'delete', width: actionColWidth },
    ];
  }

  return [
    {
      columnId: 'name',
      width: tableReference
        ? calculateWidth(tableProperty_?.length || 0, tableReference, variant)
        : 200,
      resizable: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any) => {
          const row = {
            columnId: `${data?.id}`,
            width: tableReference ? calculateColumnWidth(data?.name, variant, data) : 200,
            resizable: true,
          };
          return row;
        })
      : []),
    { columnId: 'delete', width: actionColWidth },
  ];
};

export { headerRow, footerRow, getRows, getColumns };
